/**
 * D-074 Faz 4 · PR 23: PO'nun (purchase order) okunması (plan M5). Veri ihtiyacı kuralıyla (02.11.2026) yalnız eşlemenin, kuralların, 'Use the reading'in ve 'Add to package'in
 * kullandığı değerler okunur: DOCUMENT'te 4, satır başına 4 alan (sözleşme §15, sürüm 1). Fiyat tutarları, toplamlar ve vergi okunmaz; yalnız fiyatın basılı olup olmadığı okunur (GEN-08).
 * Adres blokları, satır teslim tarihi, alıcının malzeme no'su, satıcı teklif no, şartlar, imzalar ve stok kataloğunda olmayan PN'lerin satırları okunmaz. Commercial invoice yalnız tanınır.
 * Okuma hiçbir kayda kendiliğinden yazmaz: paketler (PO no'ları dahil) ve kütük belge satırları değişmez, paket açılmaz; PO öteki paketlere yalnız kişinin satır başına onayıyla eklenir.
 * Görme sınıfı fiyatlıdır (yalnız PM ve sistem yöneticisi). Sözleşme docs/hafif-excel-sozlesmesi.md §15.
 */
import { UYARI_KALIPLARI } from "./belgeler.js";
import { KALIBRASYON_UYARI_KALIPLARI } from "./kalibrasyon.js";
import { anahtarSade } from "./malzeme-sertifikasi.js";
import { NDT_UYARI_KALIPLARI } from "./ndt-raporu.js";
import { NDT_SERTIFIKA_UYARI_KALIPLARI } from "./ndt-sertifika.js";
import { kimlikSade } from "./sarf.js";
import { yuvarla3 } from "./stok-paket.js";
import { TESLIM_OKUMA_DEGISTI_METNI, TESLIM_UYARI_KALIPLARI, birimCevir, birimKodu, miktarOku, pnSade } from "./teslim-listesi.js";
import { WELDMAP_UYARI_KALIPLARI } from "./weld-map.js";
/** Dönüşüm işinin belge türü (`donusum_isi.belge_turu`). Riser Documentation'daki `DOSYA_TUR_ADI` "satin_alma_siparisi" başka ad alanıdır. */
export const SATIN_ALMA_BELGE_TURU = "satin_alma";
/** Okunan Documents türü (GELEN_TURLERI anahtarı, "PO"; Registers grubu, fiyatlı sınıf). Sarf lotunun PO'su okunmaz (M5-36). */
export const SATIN_ALMA_GELEN_TURU = "kutuk:stok_paket:po";
export const satinAlmaGelenTuruMu = (t) => t === SATIN_ALMA_GELEN_TURU;
/** Olay kaydında nesne tipi (nesneId = satin_alma_kaydi.id). */
export const SATIN_ALMA_OLAY_NESNESI = "satin_alma_kaydi";
/** Okunan hafif excel'in belge ailesi. Hiçbir aile sorgusu bu adı almaz (Riser Documentation, kanıt zinciri ve aile işlemleri). */
export const SATIN_ALMA_GIRDI_AILESI = "Purchase-order-input";
/** DOCUMENT sayfasındaki "Document type" değeri. */
export const SATIN_ALMA_EXCEL_ADI = "Purchase order";
/** Okuma işi kodu ("PUR-6B2C3D4E"); "PO-" uyarı kodlarından ayrı. */
export const satinAlmaKodu = (kayitId) => `PUR-${kayitId.replace(/-/g, "").slice(0, 8).toUpperCase()}`;
/** Bir PO'da en çok satır (`Line`). */
export const SATIN_ALMA_SATIR_EN_COK = 100;
/**
 * Başlık alanları (DOCUMENT, 4; sabit sıra; sözleşme §15.3, sürüm 1). Her alanın tüketicisi sözleşmede ve `satin-alma-karsilastirma.ts`'te (`SATIN_ALMA_OKUNAN`) yazılıdır. `etiket`
 * hem ekran metni hem DOCUMENT'teki Field adıdır. Kimlik: PO no. Fiyat tutarı alanı yoktur; `fiyat_var` yalnız yes / no'dur.
 */
export const SATIN_ALMA_ALANLARI = [
    { alan: "belge_cinsi", etiket: "Document kind", kimlik: false },
    { alan: "po_no", etiket: "PO number", kimlik: true },
    { alan: "siparis_tarihi", etiket: "Order date", kimlik: false },
    { alan: "fiyat_var", etiket: "Prices printed", kimlik: false },
];
/** Satır alanları (ROWS, satır başına 4; sabit sıra; sözleşme §15.3). Kimlik: PN. */
export const SATIN_ALMA_SATIR_ALANLARI = [
    { alan: "po_satiri", etiket: "PO line", kimlik: false },
    { alan: "pn", etiket: "Part number", kimlik: true },
    { alan: "miktar", etiket: "Quantity", kimlik: false },
    { alan: "birim", etiket: "Unit", kimlik: false },
];
/** Alanın ekran adı (iki listede aranır); bilinmiyorsa alanın kendisi. */
export const satinAlmaEtiketi = (alan) => [...SATIN_ALMA_ALANLARI, ...SATIN_ALMA_SATIR_ALANLARI].find((a) => a.alan === alan)?.etiket ?? alan;
/** ROWS başlığı (uzun biçim: her hücre bir satır; `Line` okuma sırası). */
export const SATIN_ALMA_SATIR_BASLIKLARI = ["Line", "Field", "Value", "As written", "Page", "Certainty"];
/** Kişinin "Add to package" onayının alanı (`belge_deger_onayi.alan`; satırın parçasında, satır başına bir kez). */
export const SATIN_ALMA_EKLEME_ALANI = "kutuk_satiri";
/** `Document kind` değerleri (M5-6). */
export const SATIN_ALMA_CINSLERI = ["purchase order", "commercial invoice"];
/** M5-6: `Document kind` değeri (kırpılmış, küçük harfe çevrilmiş); başka her yazım null. */
export function satinAlmaCinsiOku(v) {
    const s = (v ?? "").trim().toLowerCase();
    return SATIN_ALMA_CINSLERI.includes(s) ? s : null;
}
/** M5-6: `Prices printed` değeri (kırpılmış, küçük harf): yes | no; başka her yazım null. */
export function satinAlmaFiyatOku(v) {
    const s = (v ?? "").trim().toLowerCase();
    return s === "yes" || s === "no" ? s : null;
}
/** GEN-04'ün ikinci değeri (commercial invoice'un yeri). */
export const SATIN_ALMA_FATURA_YERI = "Registers › Stock (Commercial invoice)";
/* ---------------- PN kökü ve PO eşitlikleri (M5-8, M5-9, M5-12) ---------------- */
/** M5-8 (saf): PN'in kökü: `pnSade`, sondaki tek bir `-` + 1-3 harf/rakam son eki atılır ("999-PN-0503-1" → "999-PN-0503"; "999-PN-0401" değişmez). */
export function satinAlmaPnKoku(v) {
    const s = pnSade(v);
    return /^(.+?)-[A-Z0-9]{1,3}$/.exec(s)?.[1] ?? s;
}
/** M5-9 (saf): iki PO no'su SIKI eşit mi (`anahtarSade` eşit ve boş değil); öneri bu eşitlikle yapılır. */
export function satinAlmaPoAyni(a, b) {
    const x = anahtarSade(a);
    return Boolean(x) && x === anahtarSade(b);
}
/** M5-12 (saf): iki PO no'su GEVŞEK eşit mi (`anahtarSade` eşit ya da biri ötekini içeriyor; ikisi de boşsa `kimlikSade` eşitliği; PR 20 TES-03 kuralı). Yalnız uyarı için. */
export function satinAlmaPoEsit(a, b) {
    const x = anahtarSade(a);
    const y = anahtarSade(b);
    if (!x || !y)
        return kimlikSade(a) === kimlikSade(b);
    return x === y || x.includes(y) || y.includes(x);
}
const degerOf = (l, alan) => l.find((x) => x.alan === alan) ?? null;
const kesinMi = (d) => Boolean(d && d.kesinlik === "kesin" && d.deger !== null && d.deger.trim() !== "");
/**
 * M5-7 (saf): her `Line` bir satırdır; satır sırası `Line`'dır. Kesin PN'li satırlar `pnSade` ile gruplanır; grubun siparişi yalnız bütün satırlarının miktarı ve birimi kesin ve
 * katalog birimine (`birimCevir`) çevrilebilirse toplamdır, değilse null ve `siparis_yok` notu. Katalogda olmayan PN'in grubu siparişsizdir (not yok; satır başka nedenle yer bulmaz).
 * Şüpheli her değer için `suphe` notu.
 */
export function satinAlmaSatirlari(satirlar, katalog) {
    const notlar = [];
    const ozetler = [...satirlar].sort((a, b) => a.no - b.no).map((s) => {
        const pn = degerOf(s.degerler, "pn");
        const poS = degerOf(s.degerler, "po_satiri");
        const mk = degerOf(s.degerler, "miktar");
        const br = degerOf(s.degerler, "birim");
        for (const d of s.degerler)
            if (d.kesinlik === "suphe")
                notlar.push({ tur: "suphe", satir: s.no, alan: d.alan });
        return {
            sira: s.no, degerler: s.degerler, pn, pnSade: kesinMi(pn) ? pnSade(pn.deger) : null,
            poSatiri: kesinMi(poS) ? poS.deger.trim() : null,
            miktar: kesinMi(mk) ? miktarOku(mk.deger) : null,
            birim: kesinMi(br) ? birimKodu(br.deger) : null,
            birimYazisi: kesinMi(br) ? br.deger.trim() : null,
        };
    });
    const gruplar = [];
    for (const s of ozetler) {
        if (!s.pnSade)
            continue;
        let g = gruplar.find((x) => x.pnSade === s.pnSade);
        if (!g) {
            g = { pnSade: s.pnSade, ilkSira: s.sira, siralar: [], poSatirlari: [], siparis: null };
            gruplar.push(g);
        }
        g.siralar.push(s.sira);
        if (s.poSatiri && !g.poSatirlari.includes(s.poSatiri))
            g.poSatirlari.push(s.poSatiri);
    }
    for (const g of gruplar) {
        const kat = katalog.find((k) => pnSade(k.pn) === g.pnSade);
        if (!kat)
            continue;
        let toplam = 0;
        let tamam = true;
        for (const sira of g.siralar) {
            const s = ozetler.find((x) => x.sira === sira);
            const c = s.miktar !== null ? birimCevir(s.miktar, s.birim, kat.birim) : null;
            if (!c) {
                tamam = false;
                break;
            }
            toplam += c.miktar;
        }
        if (tamam)
            g.siparis = { miktar: yuvarla3(toplam), birim: kat.birim };
        else
            notlar.push({ tur: "siparis_yok", pn: g.pnSade });
    }
    return { satirlar: ozetler, gruplar, notlar };
}
/* ---------------- uyarılar ---------------- */
/**
 * Kartta üretilen kodlar (satır uyarıları PO-01, PO-02, TES-03, PO-03; parçasızlar GEN-17, GEN-15, GEN-08, GEN-09, GEN-04). Hiçbiri KAPALI_UYARI_KODLARI'nda olamaz (test). GEN-06
 * kapalıdır (K15); PO-04 kural olarak sözleşmede uygulanır, uyarı olarak üretilmez (M5-20); GEN-20 aynı farkı TES-03'le ikinci kez söylerdi (M5-12).
 */
export const SATIN_ALMA_UYARI_KODLARI = ["PO-01", "PO-02", "TES-03", "PO-03", "GEN-17", "GEN-15", "GEN-08", "GEN-09", "GEN-04"];
/** Uyarı kalıpları (İngilizce anahtar; Türkçesi sözlükte). TES-03, GEN-17, GEN-15, GEN-08, GEN-09 ve GEN-04 öteki okuyucularınkiyle aynıdır. */
export const SATIN_ALMA_UYARI_KALIPLARI = {
    "PO-01": "Delivered more than ordered: {0} {1} of {2}.",
    "PO-02": "No PO for this stock package: {0}.",
    "TES-03": TESLIM_UYARI_KALIPLARI["TES-03"],
    "PO-03": "Part number differs only by suffix: {0} / {1}.",
    "GEN-17": NDT_SERTIFIKA_UYARI_KALIPLARI["GEN-17"],
    "GEN-15": WELDMAP_UYARI_KALIPLARI["GEN-15"],
    "GEN-08": UYARI_KALIPLARI["GEN-08"],
    "GEN-09": NDT_UYARI_KALIPLARI["GEN-09"],
    "GEN-04": KALIBRASYON_UYARI_KALIPLARI["GEN-04"],
};
/** Saf (M5-24): kartın durumu ve yazımın ön denetimi aynı kuraldır; ilk tutan döner. `engel` yalnız "engel" durumunda dolu. */
export function satinAlmaSatirEngeli(d) {
    const ol = (durum, engel = null) => ({ durum, engel });
    if (d.kapali)
        return ol("kapali");
    if (!d.yerVar)
        return ol("yer_yok");
    if (!d.okumaGuncel)
        return ol("engel", TESLIM_OKUMA_DEGISTI_METNI);
    if (d.engel)
        return ol("engel", d.engel);
    if (d.pakette || d.onay)
        return ol("pakette");
    if (d.acik)
        return ol("dosyalanmadi");
    return ol("eklenebilir");
}
//# sourceMappingURL=satin-alma.js.map